import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertCircle } from 'lucide-react';
import { CloudflareStreamPlayer } from '@immopersian/common';

interface RecordingPlayerProps {
  recordingUrl: string;
  webinarTitle: string;
  onClose: () => void;
}

/**
 * Recording Player Component
 *
 * Displays webinar recording using Cloudflare Stream player.
 * Shows error state if recording URL is not available.
 */
export function RecordingPlayer({ recordingUrl, webinarTitle, onClose }: RecordingPlayerProps) {
  const { t } = useTranslation();

  useEffect(() => {
    // Handle escape key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!recordingUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-white rounded-xl max-w-md w-full p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-4">
            <AlertCircle className="w-8 h-8 text-yellow-600" />
            <h2 className="text-2xl font-bold text-brand-dark">
              {t('webinar.recordingNotReady', 'Recording Not Ready')}
            </h2>
          </div>

          <p className="text-brand-dark/70 mb-6">
            {t('webinar.recordingNotReadyMessage', 'The recording for this webinar is not yet available. Please check back later or contact the admin.')}
          </p>

          <button
            onClick={onClose}
            className="w-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            {t('common.close', 'Close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-4">
            <div className="w-3 h-3 bg-brand-gold rounded-full animate-pulse"></div>
            <h1 className="text-white font-semibold text-lg truncate max-w-2xl">
              {webinarTitle}
            </h1>
            <span className="text-white/60 text-sm">
              {t('webinar.recording', 'Recording')}
            </span>
          </div>

          <button
            onClick={onClose}
            className="bg-white/10 hover:bg-white/20 text-white p-2 rounded-full transition-colors"
            aria-label={t('common.close', 'Close')}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* Recording video player */}
      <div className="w-full h-full">
        <CloudflareStreamPlayer
          videoId={recordingUrl}
          className="w-full h-full"
          autoplay={false}
          preload="auto"
          primaryColor="c14a34"
        />
      </div>
    </div>
  );
}
